import { memo, useState, useRef, useEffect } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { Link } from "react-router-dom";
import { BASE_URL } from "@/assets/constants/constants";
import circleArrow from "@/assets/images/inspections-table/circleArrow.svg";
import { Icon } from "@/components/icons/Icon";
import {
  deleteInspectionId,
  getInspectionPDF,
} from "@/lib/services/inspections";
import { useLoader } from "@/context/loader-context/LoaderContext";
import { appToast } from "@/lib/toast";
import type { ActiveInspectionRegister } from "../InspectionsLists.d";
import { useUserStore } from "@/store/useUser";
import ModalRemoveInspection from "@/views/inspections/components/modal-remove-inspection/ModalRemoveInspection";

export interface InspectionAcitivitiesProps {
  inspectionId: string | number;
  standNumber?: string;
  getNoSortedInspections: () => void;
}

const InspectionAcitivities = ({
  inspectionId,
  standNumber,
  getNoSortedInspections,
}: InspectionAcitivitiesProps) => {
  const { showLoader, hideLoader } = useLoader();
  const translate = useTranslate();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isSupervisor } = useUserStore();

  const [visibleActions, setVisibleActions] = useState(false);
  const [inspectionToRemove, setInspectionToRemove] =
    useState<ActiveInspectionRegister | null>(null);

  const modifyActionsVisibility = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setVisibleActions((prevVisibility) => !prevVisibility);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setVisibleActions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPdf = async () => {
    try {
      showLoader();
      const response = await getInspectionPDF({ id: inspectionId as number });

      const url = window.URL.createObjectURL(
        new Blob([response], { type: "application/pdf" }),
      );
      window.open(url, "_blank");
      appToast.success(translate("success"), translate("pdfDownloadSuccess"));
    } catch (error) {
      const e = error as Error;
      if (e.message === "DOCUMENT_URL_NOT_FOUND") {
        appToast.error(translate("error"), translate("pdfDownloadError"));
      } else {
        appToast.error(translate("error"), e.message);
      }
    } finally {
      hideLoader();
    }
  };

  const removeInspection = async (id: number) => {
    try {
      showLoader();
      await deleteInspectionId({ id });
      appToast.success(translate("success"), translate("inspectionRemoved"));
      getNoSortedInspections();
      setInspectionToRemove(null);
    } catch (error) {
      const e = error as Error;
      appToast.error(translate("error"), e.message);
    } finally {
      hideLoader();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {!visibleActions ? (
        <button type="button" onClick={modifyActionsVisibility}>
          <Icon name="ellipsis" className="size-6 text-ib-black" />
        </button>
      ) : (
        <div className="absolute right-0 z-10 transform flex flex-col items-end h-6 w-[180px]">
          <button type="button" onClick={modifyActionsVisibility}>
            <Icon name="closeEmptyCircle" className="size-6 text-ib-red-main" />
          </button>
          <div className="w-full bg-ib-white rounded-sm border border-ib-gray-lightest flex flex-col items-end">
            {isSupervisor ? (
              <div className="flex flex-row justify-end mt-3">
                <button
                  data-testid="trash-button"
                  onClick={() =>
                    setInspectionToRemove({
                      id: inspectionId as number,
                      snStand: standNumber as string,
                    } as unknown as ActiveInspectionRegister)
                  }
                  onTouchStart={() =>
                    setInspectionToRemove({
                      id: inspectionId as number,
                      snStand: standNumber as string,
                    } as unknown as ActiveInspectionRegister)
                  }
                  className="h-10 flex flex-row justify-end gap-x-2 ib-text-main items-center pr-4"
                >
                  {translate("remove")}
                  <Icon name="trash" className="text-ib-red-main size-6" />
                </button>
              </div>
            ) : null}

            <div className="flex flex-row justify-end mt-3">
              <button
                data-testid="pdf-button"
                value={inspectionId}
                onClick={getPdf}
                onTouchStart={getPdf}
                className="h-10 flex flex-row justify-end gap-x-2 ib-text-main items-center pr-4"
              >
                {translate("exportPdf")}
                <Icon name="downloadPdf" className="text-ib-red-main size-6" />
              </button>
            </div>
            <div className="flex flex-row justify-end mt-3 mb-3">
              <Link to={`${BASE_URL}/${inspectionId}`}>
                <button
                  data-testid="open-inspection-button"
                  className="h-10 flex flex-row justify-end gap-x-2 ib-text-main items-center pr-4"
                >
                  {translate("goToInspection")}
                  <img className="h-[24px] min-w-[20px]" src={circleArrow} />
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
      {inspectionToRemove && inspectionToRemove.id === inspectionId ? (
        <ModalRemoveInspection
          inspection={inspectionToRemove}
          closeModal={() => setInspectionToRemove(null)}
          removeInspection={removeInspection}
        />
      ) : null}
    </div>
  );
};

export default memo(InspectionAcitivities);
